import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient, useAuthStore } from "../store/authStore";
import { toast } from "sonner";

const S = {
  bg:'#0a0a0f', white:'#13131f', deep:'#0d0d18', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderMd:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a', indigo:'#7c3aed',
  grad:'linear-gradient(135deg,#7c3aed 0%,#3b82f6 100%)',
  green:'#4ade80', red:'#f87171', amber:'#fbbf24', blue:'#60a5fa',
};

const inp = { width:'100%', border:`1px solid ${S.border}`, borderRadius:8, padding:'9px 12px', fontSize:13, color:S.text, background:S.input, outline:'none', fontFamily:'inherit', boxSizing:'border-box' };
const btn = (disabled) => ({ padding:'9px 20px', background:disabled?S.deep:S.grad, color:disabled?S.hint:'#fff', border:'none', borderRadius:8, fontSize:13, fontWeight:600, cursor:disabled?'not-allowed':'pointer', fontFamily:'inherit', flexShrink:0 });
const card = { background:S.white, border:`1px solid ${S.border}`, borderRadius:10, padding:24, marginBottom:18 };

/* ── TitleRewriter ── */
function TitleRewriterTab() {
  const [title, setTitle] = useState("");
  const [keyword, setKeyword] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!title.trim()) return;
    setLoading(true); setResult(null);
    try {
      const r = await apiClient.post("/seo/rewrite-title", { title, keyword: keyword||null, language:"en" });
      setResult(r); toast.success("Title rewritten!");
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Rewrite a title for better SEO</h3>
        <input value={title} onChange={e=>setTitle(e.target.value)} placeholder="Paste your current title…"
          style={{ ...inp, marginBottom:12 }}
          onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
        <div style={{ display:'flex', gap:12 }}>
          <input value={keyword} onChange={e=>setKeyword(e.target.value)} placeholder="Target keyword (optional)"
            style={{ ...inp, flex:1 }}
            onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
          <button onClick={run} disabled={loading||!title.trim()} style={btn(loading||!title.trim())}>
            {loading?"Rewriting…":"Rewrite title"}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16, alignItems:'center' }}>
            <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:0 }}>Result</h3>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:13 }}>
              <span style={{ color:S.red, fontWeight:700 }}>{result.score_before}</span>
              <span style={{ color:S.hint }}>→</span>
              <span style={{ color:S.green, fontWeight:700 }}>{result.score_after}</span>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:12 }}>
            <div>
              <p style={{ fontSize:11, color:S.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>Before</p>
              <p style={{ fontSize:13, color:S.red, textDecoration:'line-through', background:'rgba(248,113,113,0.08)', padding:'8px 12px', borderRadius:7, margin:0 }}>{result.original_title}</p>
            </div>
            <div>
              <p style={{ fontSize:11, color:S.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 4px' }}>After</p>
              <p style={{ fontSize:13, fontWeight:600, color:S.green, background:'rgba(74,222,128,0.08)', padding:'8px 12px', borderRadius:7, margin:0 }}>{result.rewritten_title}</p>
            </div>
            <div>
              <p style={{ fontSize:11, color:S.hint, textTransform:'uppercase', letterSpacing:'0.06em', margin:'0 0 6px' }}>Changes</p>
              {result.changes_made?.map((c,i) => (
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8, fontSize:13, color:S.green, marginBottom:4 }}>
                  <span style={{ width:6, height:6, borderRadius:'50%', background:S.green, flexShrink:0 }} />{c}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ── ContentScorer ── */
function ContentScorerTab() {
  const [url, setUrl] = useState("");
  const [keyword, setKeyword] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true); setResult(null);
    try {
      const r = await apiClient.post("/seo/score", { url, target_keyword:keyword||null });
      setResult(r); toast.success(`Grade: ${r.grade}`);
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setLoading(false); }
  };

  const gradeColor = g => ({ "A+":S.green, A:S.green, B:S.blue, C:S.amber, D:'#fb923c', F:S.red }[g]||S.muted);
  const gradeBg   = g => ({ "A+":"rgba(74,222,128,0.15)", A:"rgba(74,222,128,0.15)", B:"rgba(96,165,250,0.15)", C:"rgba(251,191,36,0.15)", D:"rgba(251,146,60,0.15)", F:"rgba(248,113,113,0.15)" }[g]||"rgba(144,144,176,0.15)");
  const scoreColor = s => s>=80?S.green:s>=65?S.blue:s>=50?S.amber:S.red;

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Score any page's SEO content</h3>
        <div style={{ display:'flex', gap:12 }}>
          <input value={url} onChange={e=>setUrl(e.target.value)} placeholder="https://example.com/blog-post"
            style={{ ...inp, flex:1 }}
            onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
          <input value={keyword} onChange={e=>setKeyword(e.target.value)} placeholder="Keyword (optional)"
            style={{ ...inp, width:160 }}
            onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
          <button onClick={run} disabled={loading||!url.trim()} style={btn(loading||!url.trim())}>
            {loading?"Scoring…":"Score"}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
            <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:0 }}>Score Report</h3>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:20, fontWeight:900, padding:'4px 14px', borderRadius:8, color:gradeColor(result.grade), background:gradeBg(result.grade) }}>{result.grade}</span>
              <span style={{ fontSize:14, fontWeight:800, padding:'3px 12px', borderRadius:6, color:scoreColor(result.overall_score), background:scoreColor(result.overall_score)+'20' }}>{result.overall_score}/100</span>
            </div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:20 }}>
            {[
              { label:"Words",          value:result.content_length,           ok:result.content_length_ok },
              { label:"H2 headings",    value:result.h2_count,                 ok:result.h2_count>=3 },
              { label:"Images",         value:result.image_count,              ok:result.image_count>=1 },
              { label:"Internal links", value:result.internal_links_count,     ok:result.internal_links_count>=3 },
              { label:"Readability",    value:result.readability_score,        ok:result.readability_score>=60 },
              { label:"Kw density",     value:`${result.keyword_density}%`,    ok:result.keyword_density>=1&&result.keyword_density<=3 },
              { label:"Kw in title",    value:result.keyword_in_title?"Yes":"No", ok:result.keyword_in_title },
              { label:"Title length",   value:result.title_length_ok?"OK":"Fix", ok:result.title_length_ok },
            ].map((m,i) => (
              <div key={i} style={{ borderRadius:8, border:`1px solid ${m.ok?'rgba(74,222,128,0.3)':'rgba(248,113,113,0.3)'}`, background:m.ok?'rgba(74,222,128,0.07)':'rgba(248,113,113,0.07)', padding:'10px 12px', textAlign:'center' }}>
                <div style={{ fontSize:18, fontWeight:800, color:m.ok?S.green:S.red }}>{m.value}</div>
                <div style={{ fontSize:11, color:S.hint, marginTop:3 }}>{m.label}</div>
              </div>
            ))}
          </div>
          <div>
            <p style={{ fontSize:12, fontWeight:600, color:S.muted, margin:'0 0 8px' }}>Recommendations</p>
            {result.recommendations?.map((rec,i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, fontSize:13, color:S.muted, marginBottom:5 }}>
                <span style={{ color:S.amber, marginTop:1 }}>⚡</span>{rec}
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ── KeywordTab ── */
function KeywordTab() {
  const [topic, setTopic] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!topic.trim()) return;
    setLoading(true); setResult(null);
    try {
      const r = await apiClient.post("/seo/keywords", { topic });
      setResult(r); toast.success("Keyword clusters generated!");
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Generate semantic keyword clusters</h3>
        <div style={{ display:'flex', gap:12 }}>
          <input value={topic} onChange={e=>setTopic(e.target.value)} placeholder="Enter a topic (e.g. keto diet)"
            style={{ ...inp, flex:1 }}
            onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
          <button onClick={run} disabled={loading||!topic.trim()} style={btn(loading||!topic.trim())}>
            {loading?"Generating…":"Generate"}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Keyword Clusters</h3>
          <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
            {result.clusters?.map((cluster, i) => (
              <div key={i}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, background:S.grad, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent', textTransform:'uppercase', letterSpacing:'0.06em' }}>{cluster.intent}</span>
                  <span style={{ fontSize:11, color:S.hint }}>{cluster.volume} searches/mo</span>
                  <span style={{ fontSize:11, color:S.hint }}>· Difficulty {cluster.difficulty}</span>
                </div>
                <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                  {cluster.keywords?.map((kw,j) => (
                    <span key={j} style={{ fontSize:12, background:S.input, color:S.muted, padding:'4px 10px', borderRadius:5, border:`1px solid ${S.border}` }}>{kw}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ── DuplicateTab ── */
function DuplicateTab() {
  const [titles, setTitles] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const lines = titles.split("\n").map(l=>l.trim()).filter(Boolean);
    if (!lines.length) return;
    setLoading(true); setResult(null);
    try {
      const r = await apiClient.post("/seo/duplicates", { titles: lines });
      setResult(r); toast.success("Duplicate analysis complete!");
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Detect keyword cannibalization & near-duplicate titles</h3>
        <textarea value={titles} onChange={e=>setTitles(e.target.value)}
          placeholder={"Paste your post titles, one per line:\nBest keto recipes for weight loss\nTop 10 keto meals for beginners\nKeto diet tips for 2024"}
          style={{ ...inp, height:120, resize:'none', marginBottom:12 }}
          onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
        <div style={{ display:'flex', justifyContent:'flex-end' }}>
          <button onClick={run} disabled={loading||!titles.trim()} style={btn(loading||!titles.trim())}>
            {loading?"Analyzing…":"Detect duplicates"}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:0 }}>Duplicate Report</h3>
            <div style={{ display:'flex', gap:10 }}>
              <span style={{ fontSize:12, padding:'3px 10px', borderRadius:5, background:'rgba(248,113,113,0.12)', color:S.red }}>{result.duplicate_pairs?.length||0} conflicts</span>
              <span style={{ fontSize:12, padding:'3px 10px', borderRadius:5, background:'rgba(251,191,36,0.12)', color:S.amber }}>{result.near_duplicates?.length||0} near-dupes</span>
            </div>
          </div>
          {result.duplicate_pairs?.length > 0 && (
            <div style={{ marginBottom:16 }}>
              <p style={{ fontSize:12, fontWeight:600, color:S.red, margin:'0 0 8px' }}>Exact conflicts (cannibalization)</p>
              {result.duplicate_pairs.map((pair,i) => (
                <div key={i} style={{ background:'rgba(248,113,113,0.06)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:8 }}>
                  <p style={{ fontSize:13, color:S.text, margin:'0 0 4px' }}>{pair.a}</p>
                  <p style={{ fontSize:11, color:S.hint, margin:'0 0 4px' }}>vs</p>
                  <p style={{ fontSize:13, color:S.text, margin:'0 0 6px' }}>{pair.b}</p>
                  <p style={{ fontSize:11, color:S.red, margin:0 }}>Similarity: {pair.similarity}% · Shared keyword: <strong style={{ color:S.text }}>{pair.keyword}</strong></p>
                </div>
              ))}
            </div>
          )}
          {result.near_duplicates?.length > 0 && (
            <div>
              <p style={{ fontSize:12, fontWeight:600, color:S.amber, margin:'0 0 8px' }}>Near-duplicates</p>
              {result.near_duplicates.map((pair,i) => (
                <div key={i} style={{ background:'rgba(251,191,36,0.06)', border:'1px solid rgba(251,191,36,0.25)', borderRadius:8, padding:'10px 14px', marginBottom:8 }}>
                  <p style={{ fontSize:13, color:S.text, margin:'0 0 4px' }}>{pair.a}</p>
                  <p style={{ fontSize:11, color:S.hint, margin:'0 0 4px' }}>vs</p>
                  <p style={{ fontSize:13, color:S.text, margin:'0 0 6px' }}>{pair.b}</p>
                  <p style={{ fontSize:11, color:S.amber, margin:0 }}>Similarity: {pair.similarity}%</p>
                </div>
              ))}
            </div>
          )}
          {!result.duplicate_pairs?.length && !result.near_duplicates?.length && (
            <div style={{ textAlign:'center', padding:'24px 0' }}>
              <p style={{ fontSize:14, color:S.green, fontWeight:600, margin:0 }}>No duplicates detected!</p>
              <p style={{ fontSize:13, color:S.hint, margin:'4px 0 0' }}>All titles target distinct keywords.</p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

/* ── IndexationTab ── */
function IndexationTab() {
  const [urls, setUrls] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const list = urls.split("\n").map(u=>u.trim()).filter(Boolean);
    if (!list.length) return;
    setLoading(true); setResult(null);
    try {
      const r = await apiClient.post("/seo/indexation", { urls: list });
      setResult(r); toast.success("Indexation check complete!");
    } catch (e) { toast.error(e.message||"Failed"); }
    finally { setLoading(false); }
  };

  const statusColor = s => ({ indexed:S.green, not_indexed:S.red, blocked:S.amber, redirect:S.blue }[s]||S.hint);
  const statusBg    = s => ({ indexed:'rgba(74,222,128,0.1)', not_indexed:'rgba(248,113,113,0.1)', blocked:'rgba(251,191,36,0.1)', redirect:'rgba(96,165,250,0.1)' }[s]||'rgba(144,144,176,0.1)');

  return (
    <>
      <div style={card}>
        <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:'0 0 16px' }}>Check & recover Google indexation</h3>
        <textarea value={urls} onChange={e=>setUrls(e.target.value)}
          placeholder={"Paste URLs to check, one per line:\nhttps://example.com/post-1\nhttps://example.com/post-2"}
          style={{ ...inp, height:120, resize:'none', marginBottom:12 }}
          onFocus={e=>e.target.style.borderColor=S.indigo} onBlur={e=>e.target.style.borderColor=S.border} />
        <div style={{ display:'flex', justifyContent:'flex-end' }}>
          <button onClick={run} disabled={loading||!urls.trim()} style={btn(loading||!urls.trim())}>
            {loading?"Checking…":"Check indexation"}
          </button>
        </div>
      </div>

      {result && (
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
            <h3 style={{ fontSize:14, fontWeight:600, color:S.muted, margin:0 }}>Indexation Report</h3>
            <div style={{ display:'flex', gap:8 }}>
              <span style={{ fontSize:12, padding:'3px 10px', borderRadius:5, background:'rgba(74,222,128,0.12)', color:S.green }}>{result.indexed_count||0} indexed</span>
              <span style={{ fontSize:12, padding:'3px 10px', borderRadius:5, background:'rgba(248,113,113,0.12)', color:S.red }}>{result.not_indexed_count||0} missing</span>
            </div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            {result.results?.map((row,i) => (
              <div key={i} style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', background:S.deep, border:`1px solid ${S.border}`, borderRadius:8, padding:'12px 16px', gap:12 }}>
                <div style={{ flex:1, minWidth:0 }}>
                  <p style={{ fontSize:12, color:S.text, margin:'0 0 4px', fontFamily:"'JetBrains Mono',monospace", overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row.url}</p>
                  {row.issue && <p style={{ fontSize:11, color:S.hint, margin:0 }}>{row.issue}</p>}
                </div>
                <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:4, flexShrink:0 }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 8px', borderRadius:4, color:statusColor(row.status), background:statusBg(row.status), whiteSpace:'nowrap' }}>
                    {row.status?.replace("_"," ")}
                  </span>
                  {row.fix && (
                    <span style={{ fontSize:10, color:S.indigo, cursor:'pointer' }}>{row.fix}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ── Main Page ── */
export default function TrafficPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [tab, setTab] = useState("title");

  const tabs = [
    { id:"title",    label:"Title Rewriter" },
    { id:"score",    label:"Content Score" },
    { id:"keywords", label:"Keywords" },
    { id:"dupes",    label:"Duplicates" },
    { id:"index",    label:"Indexation" },
  ];

  return (
    <div style={{ minHeight:'100vh', background:S.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:S.text }}>
      <header style={{ background:S.white, borderBottom:`1px solid ${S.border}`, position:'sticky', top:0, zIndex:10 }}>
        <div style={{ maxWidth:920, margin:'0 auto', padding:'0 24px' }}>
          <div style={{ height:52, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:16 }}>
              <button onClick={() => navigate("/dashboard")}
                style={{ fontSize:13, color:S.muted, background:'transparent', border:`1px solid ${S.border}`, borderRadius:6, padding:'4px 10px', cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
                ← Dashboard
              </button>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:16, fontWeight:800, color:S.indigo, margin:0 }}>AI Traffic Engine</h1>
            </div>
            {user && (
              <span style={{ fontSize:10, fontWeight:700, padding:'3px 7px', borderRadius:4, background:S.bg, color:S.hint, letterSpacing:'0.06em', textTransform:'uppercase' }}>
                {user.plan||'free'}
              </span>
            )}
          </div>
          <div style={{ display:'flex', borderTop:`1px solid ${S.border}` }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding:'10px 20px', fontSize:13, fontWeight:500, background:'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?S.indigo:'transparent'}`, color:tab===t.id?S.indigo:S.muted, cursor:'pointer', whiteSpace:'nowrap' }}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main style={{ maxWidth:920, margin:'0 auto', padding:'28px 24px' }}>
        {tab === "title"    && <TitleRewriterTab />}
        {tab === "score"    && <ContentScorerTab />}
        {tab === "keywords" && <KeywordTab />}
        {tab === "dupes"    && <DuplicateTab />}
        {tab === "index"    && <IndexationTab />}
      </main>
    </div>
  );
}
